import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Github, Linkedin, Mail, Send } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Btn, Chip, GlassPanel, Kicker } from '../components/ui';

interface FormData {
  name: string;
  email: string;
  company: string;
  role: string;
  inquiry_type: string;
  message: string;
}

const INQUIRY_TYPES = ['General', 'Consulting', 'Collaboration', 'Speaking', 'Press'];

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    role: '',
    inquiry_type: 'General',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const { error } = await supabase.from('contact_submissions').insert([formData]);
      if (error) throw error;
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        role: '',
        inquiry_type: 'General',
        message: '',
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-s3 py-2.5 rounded-xs bg-bg-elev border border-border text-text text-[14px] placeholder:text-text-dim focus:outline-none focus:border-accent-alt transition-colors';

  return (
    <div className="max-w-content mx-auto px-s6 md:px-s12 py-s16 grid md:grid-cols-[1fr_320px] gap-s12">
      <div>
        <Kicker>contact · inbox open</Kicker>
        <h1 className="font-mono text-[36px] md:text-[42px] font-medium tracking-[-1.1px] mt-[14px] mb-s4 text-text">
          get in touch.
        </h1>
        <p className="text-[15px] text-text-muted leading-[1.65] max-w-[520px]">
          Prefer not to use a form? Email me directly or reach out on LinkedIn. For
          everything else, the fastest path is below.
        </p>

        <form onSubmit={handleSubmit} className="mt-s8 space-y-s4">
          {submitStatus === 'success' && (
            <GlassPanel tinted className="p-s3 flex items-start gap-2 text-ok">
              <CheckCircle size={14} className="mt-0.5 shrink-0" />
              <span className="text-[13px]">Thanks — message received. I'll reply within 72h.</span>
            </GlassPanel>
          )}
          {submitStatus === 'error' && (
            <div className="p-s3 flex items-start gap-2 text-err bg-err/10 border border-err/20 rounded-xs">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span className="text-[13px]">Something broke. Try again or email directly.</span>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-s3">
            <div>
              <label className="block font-mono text-[10px] text-text-dim uppercase tracking-[1px] mb-1.5">name</label>
              <input name="name" value={formData.name} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className="block font-mono text-[10px] text-text-dim uppercase tracking-[1px] mb-1.5">email</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className="block font-mono text-[10px] text-text-dim uppercase tracking-[1px] mb-1.5">company</label>
              <input name="company" value={formData.company} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className="block font-mono text-[10px] text-text-dim uppercase tracking-[1px] mb-1.5">role</label>
              <input name="role" value={formData.role} onChange={handleChange} className={inputClass} />
            </div>
          </div>

          <div>
            <label className="block font-mono text-[10px] text-text-dim uppercase tracking-[1px] mb-s2">inquiry type</label>
            <div className="flex flex-wrap gap-1.5">
              {INQUIRY_TYPES.map(t => (
                <Chip
                  key={t}
                  active={formData.inquiry_type === t}
                  onClick={() => setFormData(p => ({ ...p, inquiry_type: t }))}
                >
                  {t}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <label className="block font-mono text-[10px] text-text-dim uppercase tracking-[1px] mb-1.5">message</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={6}
              className={`${inputClass} resize-y min-h-[120px]`}
            />
          </div>

          <div className="flex items-center gap-s3">
            <Btn type="submit" disabled={isSubmitting} icon={<Send size={12} />}>
              {isSubmitting ? 'sending…' : 'send'}
            </Btn>
            <span className="font-mono text-[10px] text-text-dim uppercase tracking-[0.5px]">
              avg. reply &lt; 72h
            </span>
          </div>
        </form>
      </div>

      <aside className="md:sticky md:top-[80px] md:self-start">
        <GlassPanel className="p-s5" radius="xs">
          <div className="font-mono text-[10px] text-text-dim uppercase tracking-[1px] mb-s3">
            direct
          </div>
          <a
            href="https://linkedin.com/in/jhamilton01"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 py-2 text-[13px] text-text hover:text-accent transition-colors"
          >
            <Linkedin size={13} />
            <span className="font-mono">/in/jhamilton01</span>
          </a>
          <a
            href="https://github.com/eldorajo"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 py-2 text-[13px] text-text hover:text-accent transition-colors"
          >
            <Github size={13} />
            <span className="font-mono">github.com/eldorajo</span>
          </a>
          <a
            href="mailto:j.hamilton@servetdigital.com"
            className="flex items-center gap-2 py-2 text-[13px] text-text hover:text-accent transition-colors"
          >
            <Mail size={13} />
            <span className="font-mono">j.hamilton@servetdigital.com</span>
          </a>

          <div className="h-px bg-border my-s4" />
          <div className="font-mono text-[10px] text-text-dim uppercase tracking-[1px] mb-s2">
            based in
          </div>
          <div className="font-mono text-[12px] text-text-muted">charlotte, nc · est</div>
        </GlassPanel>
      </aside>
    </div>
  );
};

export default ContactPage;
